/**
 * Soft≠Live Page160 — SA Custody residual liquid (page76/130 buses + status).
 * Mount: /sa/custody · also Control · Custody (same Live desk, no Soft fork).
 * Buses:
 *   GET /sa/custody/status (always-on Live posture)
 *   GET /devices · POST /sa/devices/custody (transfer|reclaim)
 *   POST /devices/{id}/revoke · delete-reset · factory-unregister
 *   POST /factory/devices/{id}/block
 * Soft residual deleted:
 *  - sandpit form without liquid rail / KPI / Re-read / status SoT
 *  - Transfer/Reclaim without labels
 *  - Soft invent success when seat gated (honest blank)
 *  - Soft success toast when write refused (use return body only)
 */
import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico, Input, Field, Card, KV, Alert, Ticker, SOFT, FADE, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Pure: sa_device_admin / superadmin / full Access — Live grant only (status mirror). */
export function canManageCustodyOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "superadmin") return true;
  if (r === "admin" && me.platform) return true;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) =>
    /sa_device_admin|sa_full_access|sa_full/i.test(String(c || "").trim()),
  );
}

/** Pure: flatten GET /devices rows — never invent Soft owners. */
export function custodyDeviceOf(row) {
  if (!row || typeof row !== "object") return null;
  const id = String(row.device_id || row.id || "").trim();
  if (!id) return null;
  return {
    id,
    label: String(row.display_label || row.map_label || "").trim() || id,
    owner: String(row.owner_admin || row.owner || "").trim() || "—",
    group: String(row.notification_group || "").trim() || "—",
    online: row.online === true ? true : row.online === false ? false : null,
  };
}

/** Pure: register list from Live pack. */
export function custodyRegisterOf(pack) {
  const items = (pack && (pack.items || pack.devices)) || [];
  if (!Array.isArray(items)) return [];
  return items.map(custodyDeviceOf).filter(Boolean);
}

/** Pure: KPI strip — blanks until Live. */
export function custodyKpisOf(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const owners = new Set(list.map((d) => d && d.owner).filter((o) => o && o !== "—"));
  const online = list.filter((d) => d && d.online === true).length;
  const offline = list.filter((d) => d && d.online === false).length;
  return {
    devices: list.length,
    owners: owners.size,
    online,
    offline,
  };
}

const CUSTODY_ACTS = [
  {
    id: "revoke", label: "Revoke", tone: "warn", route: "revokes the device",
    say: "A security block. Its publish rights are withdrawn, so the device stops being able to publish at all. Custody is untouched — this is reversible.",
    audit: "device.revoke", undo: "restores the device",
  },
  {
    id: "reset", label: "Delete & reset", tone: "bad", route: "erases and resets the device",
    say: "Unlinks the device from the tenant and does its best to tell the board to reset itself. If the device is offline that instruction waits in the queue, and the unlink still happens.",
    audit: "device.unclaim", undo: "re-claim through the wizard",
  },
  {
    id: "factory", label: "Factory unregister", tone: "bad", route: "returns it to the factory",
    say: "Everything delete-reset does, and it rolls the factory row back to unclaimed so the serial can be claimed by a different tenant.",
    audit: "device.factory_unregister", undo: "none — the serial is free",
  },
  {
    id: "block", label: "Block at factory", tone: "bad", route: "blocks a serial at the factory",
    say: "For a returned unit. Terminal until a superadmin lifts the block by hand. Identify reports the serial as blocked and the wizard has no path forward.",
    audit: "factory.block", undo: "only a superadmin can lift it",
  },
];

export function CustodyWrite({ toast, register, onPicked, writeArmed = true }) {
  const [deviceId, setDeviceId] = useState("");
  const [toOwner, setToOwner] = useState("");
  const [confirm, setConfirm] = useState("");
  const { run, busy, error, envelope } = useAction();
  const did = () => deviceId.trim();
  const pick = (id) => {
    setDeviceId(id);
    onPicked && onPicked(id);
  };
  const needConfirm = (action) => {
    const d = did();
    if (!d) { toast("Device id is required", "bad"); return false; }
    if (confirm.trim() !== d) {
      toast("Type the device id to confirm " + action, "warn");
      return false;
    }
    return true;
  };
  const submitCustody = async (action) => {
    if (!writeArmed) {
      toast("Custody write is closed on this seat", "warn");
      return;
    }
    const d = did();
    if (!d) { toast("Device id is required", "bad"); return; }
    if (action === "transfer" && !toOwner.trim()) {
      toast("Transfer needs a destination administrator", "bad");
      return;
    }
    const body = { action, device_id: d };
    if (action === "transfer") body.to_owner_admin = toOwner.trim();
    const out = await run(path("post__sa_devices_custody"), { method: "POST", body });
    if (!out) {
      toast(error || "Custody write refused — nothing was changed", "bad");
      return;
    }
    toast(
      action === "transfer"
        ? `Custody of ${d} transferred to ${toOwner.trim()} · audit row written`
        : `Device ${d} reclaimed to platform · audit row written`,
      action === "transfer" ? "go" : "warn",
    );
    register && register.reload && register.reload();
  };
  const danger = async (kind) => {
    if (!writeArmed) {
      toast("Danger write is closed on this seat", "warn");
      return;
    }
    if (!needConfirm(kind)) return;
    const d = did();
    const map = {
      revoke: ["post__devices_p_revoke", "Publishing revoked · custody untouched"],
      reset: ["post__devices_p_delete-reset", "Delete & reset requested"],
      factory: ["post__devices_p_factory-unregister", "Factory unregister"],
    };
    const row = map[kind];
    if (!row) return;
    const out = await run(path(row[0], { pathParams: [d] }), { method: "POST", body: {} });
    if (!out) {
      toast(error || kind + " refused — nothing was changed", "bad");
      return;
    }
    toast(row[1] + " · " + d, "warn");
    setConfirm("");
    register && register.reload && register.reload();
  };
  const blockAtFactory = async () => {
    if (!writeArmed) {
      toast("Factory block is closed on this seat", "warn");
      return;
    }
    if (!needConfirm("block")) return;
    const d = did();
    const out = await run(path("post__factory_devices_p_block", { pathParams: [d] }), { body: {} });
    if (!out) {
      toast(error || "Factory block refused — nothing was changed", "bad");
      return;
    }
    toast("Serial blocked at factory · " + d, "warn");
    setConfirm("");
  };
  const rows = useMemo(() => custodyRegisterOf(register && register.data), [register && register.data]);

  return html`
    <${Card} title="Device custody write" meta="Custody write"
        sub="Transfer and reclaim run on the live custody write bus. Revoke / reset / unregister use the same device danger controls as the estate desk — success is never invented.">
      ${rows.length ? html`
        <div className="rows sa-custody-desk__pick" style=${{ maxHeight: 160, overflow: "auto", marginBottom: 10 }}>
          ${rows.slice(0, 40).map((r) => html`
            <button key=${r.id} type="button" className="row"
              data-sel=${deviceId === r.id ? "1" : "0"}
              onClick=${() => pick(r.id)}>
              <span className="st"/>
              <span className="main">
                <b>${r.label}</b>
                <span>${r.id} · owner ${r.owner}${r.online === true ? " · online" : r.online === false ? " · offline" : ""}</span>
              </span>
            </button>`)}
        </div>` : html`<p className="note">Register empty or gated — type a device id.</p>`}
      <${Field} label="Device">
        <${Input} icon="chip" placeholder="CS-0A41" value=${deviceId}
          onChange=${(e) => setDeviceId(e.target.value)}/><//>
      <${Field} label="Move to" hint="Leave empty to reclaim the device to the platform.">
        <${Input} icon="user" placeholder="another.admin" value=${toOwner}
          onChange=${(e) => setToOwner(e.target.value)}/><//>
      <${Field} label="Type device id to confirm dangerous acts"
          hint="Required for revoke, reset, unregister and factory block.">
        <${Input} icon="alert" value=${confirm} onChange=${(e) => setConfirm(e.target.value)}
          placeholder="must match device id"/><//>
      ${error ? html`<${Alert} tone="warn" title="Last custody write failed" say=${error}/>` : null}
      ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
      <div className="btn-row" style=${{ flexWrap: "wrap" }}>
        <button className="btn btn--ghost btn--sm" disabled=${busy || !writeArmed}
          onClick=${() => submitCustody("transfer")}>Transfer</button>
        <button className="btn btn--danger btn--sm" disabled=${busy || !writeArmed}
          onClick=${() => submitCustody("reclaim")}>Reclaim</button>
        <button className="btn btn--ghost btn--sm" disabled=${busy || !writeArmed}
          onClick=${() => danger("revoke")}>Revoke</button>
        <button className="btn btn--danger btn--sm" disabled=${busy || !writeArmed}
          onClick=${() => danger("reset")}>Delete & reset</button>
        <button className="btn btn--danger btn--sm" disabled=${busy || !writeArmed}
          onClick=${() => danger("factory")}>Factory unregister</button>
        <button className="btn btn--danger btn--sm" disabled=${busy || !writeArmed}
          onClick=${blockAtFactory}>Block at factory</button>
      </div>
      <p className="note">Open the device page for context: <button className="btn btn--text btn--sm" style=${{ padding: 0 }}
        onClick=${() => (did() ? go("/machine/" + did()) : toast("Enter a device id first", "warn"))}>
        Fleet · device</button></p>
    <//>`;
}

export function SaCustodyDesk({ toast }) {
  const { me } = useSession();
  const posture = useApi(path("get__sa_custody_status"));
  const post = posture.data || {};
  // Live write_allowed only — client role invent of open write seat deleted.
  const seat = (posture.loading || !posture.data) ? false : (post.write_allowed === true);
  const detail = post.detail || null;
  const devices = useApi(seat ? path("get__devices") : "");
  const kpis = useMemo(() => custodyKpisOf(custodyRegisterOf(devices.data)), [devices.data]);
  const [act, setAct] = useState(null);
  const denied = !!(devices.error && /\b40[13]\b|forbidden|insufficient|capability/i.test(String(devices.error)));

  const reRead = () => {
    posture.reload && posture.reload();
    devices.reload && devices.reload();
    toast && toast("Custody posture + register re-read", "go");
  };

  if (!seat) {
    return html`<div className="sa-custody-desk sa-custody-desk--liquid"
      data-cmc-page="page130-sa-custody" data-cmc-legacy="page76-sa-custody"
      data-cmc-residual="page160-custody">
      <div className="sa-custody-desk__rail">
        <div>
          <p className="eyebrow">Platform · Custody</p>
          <h2 className="sa-custody-desk__title">Seat closed</h2>
          <p className="note">Transfer and reclaim stay sealed until this seat opens.</p>
        </div>
        <div className="btn-row sa-custody-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading}>Re-read posture</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/audit")}>Audit</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/fleet/health")}>Fleet health</button>
        </div>
      </div>
      <${Card} title="Custody posture" meta="Live">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${String(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            Write seat closed · ${detail || "not available on this account"}
          </p>` : null}
      <//>
      <${Alert} tone="info" title="Custody administration not on this account"
        say=${"Transfer / reclaim need superadmin or sa_device_admin / sa_full_access. This account is "
          + (me && me.role ? me.role : "operator")
          + " — nothing is invented to fill the gap. server posture."}/>
      <${Card} title="Write form" meta="gated">
        <p className="note">No custody writes are offered on this grant. Success is forbidden without server confirmation.</p>
      <//>
    </div>`;
  }

  return html`<div className="sa-custody-desk sa-custody-desk--liquid"
    data-cmc-page="page130-sa-custody" data-cmc-legacy="page76-sa-custody"
    data-cmc-residual="page160-custody">
    <div className="sa-custody-desk__rail">
      <div>
        <p className="eyebrow">Platform · Custody</p>
        <h2 className="sa-custody-desk__title">Transfer · Reclaim</h2>
        <p className="note">
          High-speed custody actions. Brochure Explain cards never write.
          Success only after the server confirms.
        </p>
      </div>
      <div className="btn-row sa-custody-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${devices.loading || posture.loading}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/factory")}>Factory</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/tenant")}>Tenants</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/audit")}>Audit</button>
      </div>
    </div>

    <${Card} title="Custody posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-custody-desk__kpis" role="group" aria-label="Custody posture">
          ${[
            ["Seat", seat ? "open" : "gated", seat ? "1" : "0", "status Live"],
            ["Transfer", post.transfer_allowed === true ? "armed" : "—", post.transfer_allowed === true ? "1" : "0", "transfer"],
            ["Danger", post.danger_allowed === true ? "armed" : "—", post.danger_allowed === true ? "1" : "0", "revoke/reset"],
            ["Brochure", "UI only", "0", "Explain never writes"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-custody-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          Write and list only when seats open · nothing is invented locally
        </p>` : null}
    <//>

    <${Alert} tone="warn" icon="alert" title="Explain is documentation — writes use the form"
      say="The cards below do not call the server. Transfer / reclaim / revoke / reset / unregister / block only succeed through the write form (or the device Danger page). Invented success toasts are forbidden."/>

    ${devices.error ? html`
      <${Alert} tone=${denied ? "warn" : "bad"} title=${denied ? "Register gated" : "Register read failed"}
        say=${String(devices.error)}/>` : null}

    <div className="sa-custody-desk__kpis" role="group" aria-label="Custody KPIs">
      ${[
        ["Devices", kpis.devices, kpis.devices ? "1" : "0", "Register"],
        ["Owners", kpis.owners, kpis.owners ? "1" : "0", "distinct Live"],
        ["Online", kpis.online, kpis.online ? "1" : "0", "from presence flag"],
        ["Offline", kpis.offline, kpis.offline ? "1" : "0", "from presence flag"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="sa-custody-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>${em}</em>
        </div>`)}
    </div>

    <${CustodyWrite} toast=${toast} register=${devices} writeArmed=${seat}/>

    <${Card} title="What each one actually does" meta="brochure · not a write surface">
      ${CUSTODY_ACTS.map((a) => html`
        <div key=${a.id} className="cust">
          <div className="cust__l">
            <b data-tone=${a.tone}>${a.label}</b>
            <code>${a.route}</code>
          </div>
          <div className="cust__m">
            <p>${a.say}</p>
            <div className="cust__f">
              <span className="chip" data-tone="off">${a.audit}</span>
              <span className="cust__u">undo · ${a.undo}</span>
            </div>
          </div>
          <button className=${"btn btn--sm " + (a.tone === "bad" ? "btn--danger" : "btn--ghost")}
            onClick=${() => setAct(a)}>Explain · UI</button>
        </div>`)}
    <//>

    <${Card} title="Who may do it" meta="not simply admin">
      <div className="rows">
        ${[["Owner admin", "Holds custody. May revoke, reset and unregister their own devices."],
           ["can_operate grantee", "May send commands to a shared device and may not revoke it. The share does not carry custody."],
           ["Delegated staff", "Needs sa_device_admin under the singular root SA — time-boxed. Not a second SA."],
           ["Root SA (superadmin)", "Global. Exactly one. Cross-tenant targets must be named explicitly."]].map(([t, s]) => html`
          <div key=${t} className="row row--say"><${Ico} d="shield" w=${14}/>
            <span className="main"><b>${t}</b><span>${s}</span></span></div>`)}
      </div>
      <p className="note">The revoke route runs an extra check beyond role — a grantee who can operate a
        device is deliberately stopped from revoking the owner's hardware out from under them.</p>
    <//>

    <${Card} title="Unbind jobs" meta="what a failed reset leaves behind">
      <p className="sub">A reset that could not reach the device leaves a job behind. Claiming the same
        board again settles that job automatically as <code>reclaimed</code>, rather than leaving it
        hanging.</p>
      <div className="chips">
        ${["completed", "failed", "aborted", "cancelled"].map((s) => html`
          <span key=${s} className="chip" data-tone="off">${s}</span>`)}
      </div>
      <p className="note">Those four are terminal. Anything else is still in flight.</p>
    <//>

    <${AnimatePresence}>
      ${act && html`
        <${M.div} key="a" className="scrim" initial=${{ opacity: 0 }} animate=${{ opacity: 1 }}
            exit=${{ opacity: 0 }} transition=${FADE} onClick=${() => setAct(null)}>
          <${M.div} className="sheetlet glass" initial=${{ scale: 0.97, y: 10 }} animate=${{ scale: 1, y: 0 }}
              exit=${{ scale: 0.98, y: 6 }} transition=${SOFT} onClick=${(e) => e.stopPropagation()}>
            <div className="sheetlet__in">
              <${Alert} tone=${act.tone === "bad" ? "bad" : "warn"} icon="alert" title=${act.label}
                say=${act.say + " This Explain panel never writes. Use the write form above (type device id to confirm) — invented success is forbidden."}/>
              <${KV} k="what it does" v=${act.route}/>
              <${KV} k="audit key" v=${act.audit}/>
              <${KV} k="reversible" v=${act.undo} tone=${act.undo === "none — the serial is free" ? "bad" : "warn"}/>
              <div className="btn-row sa-custody-desk__acts">
                <button className="btn btn--sm btn--go"
                  onClick=${() => { setAct(null); toast("Use the custody write form above", "warn"); }}>
                  Back to write form</button>
                ${act.id === "block" ? html`<button className="btn btn--ghost btn--sm"
                  onClick=${() => { setAct(null); go("/sa/factory"); }}>Open Factory desk</button>` : null}
                <button className="btn btn--text" onClick=${() => setAct(null)}>Close · UI</button>
              </div>
            </div>
          <//>
        <//>`}
    <//>
  </div>`;
}

/** Thin mount alias — sa.js / Control both import Custody. */
export function Custody({ toast }) {
  return html`<${F} data-cmc-page="page76-sa-custody-mount">
    <${SaCustodyDesk} toast=${toast}/>
  <//>`;
}
